import { useState, useEffect, useMemo, useCallback } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { Upload, Image, X, ArrowLeft, Save, FileText, Type, Loader2 } from "lucide-react";
import { getPublication } from "../../API/getters";
import api from "../../API/api";
import { notification } from "antd";
import useFormDraft from "../../hooks/useFormDraft";

const MAX_IMAGE_DRAFT_CHARS = 2 * 1024 * 1024;

export default function EditPublicationPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const location = useLocation();

    const [title, setTitle] = useState("");
    const [text, setText] = useState("");
    const [image, setImage] = useState(null);
    const [fileImage, setFileImage] = useState(null);
    const [loading, setLoading] = useState(false);
    const [hasImage, setHasImage] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [draftImagePreview, setDraftImagePreview] = useState(null);

    const draftData = useMemo(
        () => ({
            title,
            text,
            imagePreview:
                draftImagePreview && draftImagePreview.length <= MAX_IMAGE_DRAFT_CHARS
                    ? draftImagePreview
                    : null,
        }),
        [title, text, draftImagePreview],
    );

    const setDraftData = useCallback((data) => {
        setTitle(data.title ?? "");
        setText(data.text ?? "");
        if (data.imagePreview) {
            setDraftImagePreview(data.imagePreview);
            setImage(data.imagePreview);
            setHasImage(true);
            setFileImage(null);
        }
    }, []);

    const {
        markEdited,
        clearDraft,
        draftModals,
        draftBanner,
    } = useFormDraft({
        namespace: "publicacao-editar",
        formId: id,
        data: draftData,
        setData: setDraftData,
        enabled: true,
        ready: !isLoading,
        notice:
            draftImagePreview && draftImagePreview.length > MAX_IMAGE_DRAFT_CHARS
                ? "A imagem é demasiado grande para o rascunho local; apenas o texto foi guardado."
                : undefined,
    });

    useEffect(() => {
        async function load() {
            setIsLoading(true);
            try {
                if (location.state?.publication) {
                    const pub = location.state.publication;
                    setTitle(pub.title);
                    setText(pub.text || pub.description || "");
                    setImage(pub.image || null);
                    setHasImage(!!pub.image);
                } else {
                    const data = await getPublication(id);
                    setTitle(data.title);
                    setText(data.description);
                    setImage(data.image);
                    setHasImage(!!data.image);
                }
            } catch (error) {
                console.error("Erro ao carregar publicação:", error);
                notification.error({
                    message: "Erro",
                    description: "Não foi possível carregar a publicação.",
                    placement: "topRight",
                });
            } finally {
                setIsLoading(false);
            }
        }

        load();
    }, [id, location.state]);

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        markEdited();
        setFileImage(file);
        setImage(URL.createObjectURL(file));
        setHasImage(true);

        const reader = new FileReader();
        reader.onloadend = () => {
            setDraftImagePreview(reader.result);
        };
        reader.readAsDataURL(file);
    };

    const handleRemoveImage = () => {
        markEdited();
        setImage(null);
        setFileImage(null);
        setDraftImagePreview(null);
        setHasImage(false);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        const formData = new FormData();
        formData.append("title", title);
        formData.append("description", text);

        if (fileImage) {
            formData.append("image", fileImage);
        }

        try {
            const response = await api.put(`v1/publications/${id}/`, formData);

            if (response.status === 200) {
                await clearDraft();
                notification.success({
                    message: "Sucesso",
                    description: "Publicação atualizada com sucesso!",
                    placement: "topRight",
                });
                navigate("/minhas-publicacoes");
            } else {
                notification.error({
                    message: "Erro",
                    description: "Erro ao atualizar a publicação.",
                    placement: "topRight",
                });
            }
        } catch (error) {
            notification.error({
                message: "Erro",
                description: "Ocorreu um erro ao atualizar a publicação.",
                placement: "topRight",
            });
        } finally {
            setLoading(false);
        }
    };

    if (isLoading) {
        return (
            <div className="w-full py-6">
                <div className="flex flex-col items-center justify-center min-h-[60vh]">
                    <Loader2 className="w-8 h-8 text-emerald-500 animate-spin mb-3" />
                    <span className="text-gray-400 text-sm">Carregando publicação...</span>
                </div>
            </div>
        );
    }

    return (
        <div className="w-full py-6">
            {draftModals}
            {/* Botão Voltar */}
            <button
                onClick={() => navigate("/minhas-publicacoes")}
                className="inline-flex items-center gap-2 text-gray-500 hover:text-emerald-600 transition-colors text-sm mb-6"
            >
                <ArrowLeft className="w-4 h-4" />
                Voltar
            </button>

            {/* Form Card - Alargado */}
            <div className="w-full">
                <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                    <form onSubmit={handleSubmit} className="p-8 md:p-10 space-y-7">
                        {draftBanner}
                        {/* Título */}
                        <div>
                            <label className="flex items-center gap-2 text-sm font-medium text-gray-600 mb-2">
                                <Type className="w-4 h-4 text-emerald-500" />
                                Título
                            </label>
                            <input
                                type="text"
                                value={title}
                                onChange={(e) => {
                                    markEdited();
                                    setTitle(e.target.value);
                                }}
                                placeholder="Digite o título da publicação..."
                                className="w-full rounded-xl border border-gray-200 px-5 py-3.5 text-base focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all"
                                required
                            />
                        </div>

                        {/* Descrição */}
                        <div>
                            <label className="flex items-center gap-2 text-sm font-medium text-gray-600 mb-2">
                                <FileText className="w-4 h-4 text-emerald-500" />
                                Descrição
                            </label>
                            <textarea
                                value={text}
                                onChange={(e) => {
                                    markEdited();
                                    setText(e.target.value);
                                }}
                                placeholder="Escreva uma descrição detalhada..."
                                rows={8}
                                className="w-full rounded-xl border border-gray-200 px-5 py-3.5 text-base focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all resize-none"
                                required
                            />
                        </div>

                        {/* Imagem Atual */}
                        {hasImage && image && (
                            <div>
                                <label className="flex items-center gap-2 text-sm font-medium text-gray-600 mb-2">
                                    <Image className="w-4 h-4 text-emerald-500" />
                                    Imagem Atual
                                </label>
                                <div className="relative rounded-xl overflow-hidden border border-gray-200 bg-gray-50">
                                    <img
                                        src={image}
                                        alt="Preview"
                                        className="w-full h-72 object-cover"
                                    />
                                    <button
                                        type="button"
                                        onClick={handleRemoveImage}
                                        className="absolute top-3 right-3 bg-red-500 text-white rounded-full p-2 hover:bg-red-600 transition-colors shadow-md"
                                    >
                                        <X className="w-5 h-5" />
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* Upload */}
                        <div>
                            <label className="flex items-center gap-2 text-sm font-medium text-gray-600 mb-2">
                                <Upload className="w-4 h-4 text-emerald-500" />
                                {hasImage ? "Alterar Imagem" : "Adicionar Imagem (Opcional)"}
                            </label>
                            <label className="flex items-center justify-center gap-3 bg-gray-50 hover:bg-gray-100 border border-gray-200 rounded-xl px-5 py-4 cursor-pointer transition-colors">
                                <Upload className="w-5 h-5 text-emerald-500" />
                                <span className="text-base text-gray-600">
                                    Escolher Nova Imagem
                                </span>
                                <input
                                    type="file"
                                    accept="image/*"
                                    onChange={handleImageChange}
                                    className="hidden"
                                />
                            </label>
                            <p className="text-xs text-gray-400 mt-2">
                                Formatos aceitos: JPG, PNG, GIF (max. 5MB)
                            </p>
                        </div>

                        {/* Botões */}
                        <div className="flex gap-4 pt-6 border-t border-gray-100">
                            <button
                                type="button"
                                onClick={() => navigate("/minhas-publicacoes")}
                                className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium py-3.5 rounded-xl text-base transition-colors"
                            >
                                Cancelar
                            </button>
                            <button
                                type="submit"
                                disabled={loading}
                                className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white font-medium py-3.5 rounded-xl text-base transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {loading ? (
                                    <>
                                        <Loader2 className="w-5 h-5 animate-spin" />
                                        <span>Salvando...</span>
                                    </>
                                ) : (
                                    <>
                                        <Save className="w-5 h-5" />
                                        <span>Salvar Alterações</span>
                                    </>
                                )}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
}